import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

/**
 * The page ordinal, held in the URL and clamped to a page that exists.
 *
 * Promoted to the shared ring on its **sixth** surface. The content list, the
 * account register, the comment queue, the report queue, the case queue and the
 * notification inbox all walk a server-paged list, and three separate things
 * were going wrong across them:
 *
 * - **Four of the six held the ordinal in component state.** A deep link always
 *   landed on page one, a reload threw the reader back to page one, and «page 3
 *   of the queue» was not a thing anybody could send to a colleague. On a
 *   moderation queue that is not an inconvenience: a comment on page two is
 *   invisible to everyone but its author until somebody judges it.
 * - **Two of the six held it in the URL and clamped**, and the other of those
 *   two did not — `/content?page=999` drew «صفحة ٩٩٩ من ٥» over page one's rows,
 *   a location that does not exist presented as a valid one.
 * - The clamp that did exist was written twice, which is two places for one
 *   rule to drift.
 *
 * **Page one is the absence of the parameter**, never `?page=1` written out: one
 * list has one address, so a shared link and a fresh arrival are the same URL.
 *
 * **The clamp `replace`s**, so the back button returns to wherever the reader
 * came from rather than to the address that was just corrected. It runs only
 * once the page count is known — before the first response there is nothing to
 * compare against, and guessing would fight the reader's own navigation.
 */
export function usePageParam(): {
  /** One-based, and never below one however the URL was typed. */
  page: number;
  goToPage: (target: number) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  // A page below one, or anything that is not a number, is page one — the URL
  // is a value a reader types, and a typed `?page=0` must not be sent as an
  // offset the service has to defend against.
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);

  function goToPage(target: number) {
    const next = new URLSearchParams(searchParams);
    if (target <= 1) next.delete('page');
    else next.set('page', String(target));
    setSearchParams(next);
  }

  return { page, goToPage };
}

/**
 * Bring an out-of-range ordinal back to the last page that exists.
 *
 * Separate from the reader above because the page count is only known *after*
 * the list has been read with the ordinal this hook is checking — one hook
 * taking both would have to be called before the value it needs exists.
 *
 * `replace`, so the back button returns to wherever the reader came from rather
 * than to the address that was just corrected. It does nothing until the count
 * is known: before the first response there is nothing to compare against, and
 * guessing would fight the reader's own navigation.
 */
export function useClampPage(page: number, pageCount: number | undefined): void {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (pageCount === undefined || page <= pageCount) return;

    const next = new URLSearchParams(searchParams);
    if (pageCount <= 1) next.delete('page');
    else next.set('page', String(pageCount));
    setSearchParams(next, { replace: true });
  }, [page, pageCount, searchParams, setSearchParams]);
}

/**
 * How many pages a total divides into, or `undefined` when the service declared
 * no total or a page size of zero.
 *
 * A figure invented here would be worse than the absence: the pager draws «صفحة
 * ٢ من ٧» from it, and a keyset list deliberately buys its speed by not running
 * a count query at all.
 */
export function pageCountOf(total: number | undefined, pageSize: number | undefined): number | undefined {
  if (total === undefined || pageSize === undefined || pageSize <= 0) return undefined;
  return Math.max(1, Math.ceil(total / pageSize));
}
